import { Component, Input, ViewChild, OnDestroy, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { AdminService } from '../../../../../services/admin.service';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UntypedFormBuilder, FormGroup } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { BackOffice24StatesService } from '../../state/back-office24-states.service';
import { catchError, map, merge, of, startWith, Subscription, switchMap, finalize, takeUntil } from 'rxjs';
import { MyConfigOrganization } from '../../../../../models/myConfig';
import { Order, OrderPaginationModel } from '../../../../../models/orderModel';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-back-office24-order-list-pagination',
  standalone: false,
  templateUrl: './back-office24-order-list-pagination.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './back-office24-order-list-pagination.component.css'
})
export class BackOffice24OrderListPaginationComponent implements OnDestroy {
  private orgSubscription: Subscription = new Subscription();
  private destroy$ = new Subject<void>();

  @Input() organization: MyConfigOrganization;

  displayedColumns: string[] = [
    'orderNr',
    'organizationName',
    'carId',
    'customerName',
    'cellular',
    'email',
    'comment',
    'parkingFrom',
    'parkingTo',
    'orderDate',
    'status'
  ];

  @Output() incrementRequests: EventEmitter<void> = new EventEmitter<void>();
  @Output() decrementRequests: EventEmitter<void> = new EventEmitter<void>();

  @Input() isLoading: boolean = false;
  activeRequests: number = 0;
  orders: Order[] = [];
  ordersLastUpdated: any;
  ordersASLastUpdated: any;
  dataSource: MatTableDataSource<Order>;
  availableSubOrgs: any[] = [];

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) set matSort(sort: MatSort) {
    if (sort) {
      this.sort = sort;
      this.setupSorting();
    }
  }
  sort: MatSort;

  totalData: number = 0;
  pageSizes: number[] = [10, 25, 50, 100];

  // Map frontend column names to backend column names
  private readonly columnMapping: { [key: string]: string } = {
    'orderNr': 'OrderNr',
    'carId': 'RegNo',
    'customerName': 'Name',
    'cellular': 'Phone',
    'email': 'Email',
    'comment': 'Comment',
    'parkingFrom': 'ParkingFrom',
    'parkingTo': 'ParkingTo',
    'orderDate': 'Date',
    'status': 'Status'
  };

  // Filter properties
  filtersExpanded: boolean = true;

  // Search properties
  selectedSearchType: string = 'orderNr';
  currentSearchValue: string = '';
  searchQuery: string = '';
  searchValidationError: boolean = false;

  // Multi-select status
  selectedStatuses: number[] = [];
  selectedStatus: number | '' = '';

  selectedOrgs: string[] = [];


  // Date range FormGroups
  createdDateRange: FormGroup;
  bookingBeginRange: FormGroup;
  bookingEndRange: FormGroup;

  // Status options
  statusOptions: any[] = [
    { value: 1, name: 'New' },
    { value: 2, name: 'Checkout' },
    { value: 4, name: 'Paid' },
    { value: 5, name: 'DriveUp' },
    { value: 6, name: 'CheckoutAdditional' },
    { value: 8, name: 'Delivered' },
    { value: 16, name: 'Return' },
    { value: 32, name: 'Done' },
    { value: 64, name: 'Cancelled' }
  ];

  orderTypeOptions: any[] = [
    { value: 'subscription', name: 'Subscription', translation: "ORDER_LIST.TYPES.SUBSCRIPTION" },
    { value: 'driveIn', name: 'DriveIn', translation: "ORDER_LIST.TYPES.DRIVEIN" }
  ]
  selectedOrderTypes: string[] = [];

  constructor(
    public adminService: AdminService,
    public backofficeService: BackOfficeService,
    public dialog: MatDialog,
    public router: Router,
    private fb: UntypedFormBuilder,
    public datepipe: DatePipe,
    public backOffice24StatesService: BackOffice24StatesService,
  ) {
    this.createdDateRange = this.fb.group({
      start: [{ value: null, disabled: this.isLoading }],
      end: [{ value: null, disabled: this.isLoading }]
    });

    this.bookingBeginRange = this.fb.group({
      start: [{ value: null, disabled: this.isLoading }],
      end: [{ value: null, disabled: this.isLoading }]
    });

    this.bookingEndRange = this.fb.group({
      start: [{ value: null, disabled: this.isLoading }],
      end: [{ value: null, disabled: this.isLoading }]
    });

    // Subscribe to date range changes (but don't auto-trigger API calls)
    // this.createdDateRange.valueChanges.subscribe(() => {
    //   // Just update the filter count, don't auto-trigger API
    // });

    // this.bookingBeginRange.valueChanges.subscribe(() => {
    //   // Just update the filter count, don't auto-trigger API
    // });

    // this.bookingEndRange.valueChanges.subscribe(() => {
    //   // Just update the filter count, don't auto-trigger API
    // });
  }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => this.organization = org
    );
    this.getSubOrgs();
    this.setOrderMatTableData();
  }

  getSubOrgs() {
    this.organization.config.subOrganizations.forEach(org => {
      if (org.typeName !== "ServicePerformer") {
        this.availableSubOrgs.push({
          id: org.organizationId,
          label: org.name
        });
      }
    });
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.orgSubscription.unsubscribe();
  }

  onSearchTypeChange(): void {
    this.currentSearchValue = '';
    this.searchValidationError = false;
    // Don't trigger API call automatically
  }

  onSearchValueChange(): void {
    this.searchValidationError = false;
  }

  executeSearch(): void {
    if (!this.currentSearchValue?.trim()) return;

    if (!this.isSearchValueValid()) {
      this.searchValidationError = true;
      return;
    }

    this.searchValidationError = false;
    this.applyFilters();
  }

  isSearchValueValid(): boolean {
    if (this.selectedSearchType === 'name') {
      return this.currentSearchValue?.trim().length >= 3;
    }
    return true;
  }

  // Clear search
  clearSearch(): void {
    this.searchValidationError = false;
    this.currentSearchValue = '';
    this.applyFilters();
  }


  getSearchPlaceholder(): string {
    switch (this.selectedSearchType) {
      case 'common': return 'ORDER_LIST.SEARCH_PLACEHOLDER_ANY_FIELD';
      case 'orderNr': return 'ORDER_LIST.SEARCH_PLACEHOLDER_ORDER_NR';
      case 'transactionNr': return 'ORDER_LIST.SEARCH_PLACEHOLDER_TRANSACTION_NR';
      case 'name': return 'ORDER_LIST.SEARCH_PLACEHOLDER_NAME';
      case 'email': return 'ORDER_LIST.SEARCH_PLACEHOLDER_EMAIL';
      case 'phone': return 'ORDER_LIST.SEARCH_PLACEHOLDER_PHONE';
      case 'registration': return 'ORDER_LIST.SEARCH_PLACEHOLDER_REGISTRATION';
      default: return 'ORDER_LIST.SEARCH_PLACEHOLDER';
    }
  }

  getSearchIcon(): string {
    switch (this.selectedSearchType) {
      case 'common': return 'search';
      case 'orderNr': return 'tag';
      case 'transactionNr': return 'account_balance_wallet';
      case 'name': return 'person';
      case 'email': return 'email';
      case 'phone': return 'phone';
      case 'registration': return 'directions_car';
      default: return 'search';
    }
  }

  getSearchType() {
    switch (this.selectedSearchType) {
      case 'common': return 'text';
      case 'orderNr': return 'number';
      case 'transactionNr': return 'number';
      case 'name': return 'text';
      case 'email': return 'text';
      case 'phone': return 'text';
      case 'registration': return 'text';
      default: return 'text';
    }
  }

  // Backward compatibility methods use if you want
  onSearchChange(event: any): void {
    this.searchQuery = event.target.value;
    this.applyFilters();
  }

  onStatusChange(): void {
    this.applyFilters();
  }

  onDateFilterChange(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    // Cancel any ongoing requests
    this.destroy$.next();

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    // this.loadData();
    this.setOrderMatTableData();
  }

  // Clear all filters
  clearFilters(): void {
    this.currentSearchValue = '';
    this.searchQuery = '';
    this.searchValidationError = false;
    this.selectedSearchType = 'orderNr';
    this.selectedStatuses = [];
    this.selectedOrderTypes = [];
    this.selectedOrgs = []
    this.selectedStatus = '';
    this.createdDateRange.reset();
    this.bookingBeginRange.reset();
    this.bookingEndRange.reset();
    this.applyFilters();
  }


  getActiveFiltersCount(): number {
    let count = 0;
    if (this.currentSearchValue?.trim()) count++;
    if (this.searchQuery?.trim()) count++;
    if (this.selectedStatuses.length > 0) count++;
    if (this.selectedOrderTypes.length > 0) count++;
    if (this.selectedOrgs.length > 0) count++;
    if (this.selectedStatus !== '') count++;
    if (this.createdDateRange.get('start')?.value || this.createdDateRange.get('end')?.value) count++;
    if (this.bookingBeginRange.get('start')?.value || this.bookingBeginRange.get('end')?.value) count++;
    if (this.bookingEndRange.get('start')?.value || this.bookingEndRange.get('end')?.value) count++;
    return count;
  }


  setFilters({
    orderNos = null,
    orderIds = null,
    sortingColumn = 'orderNr',
    sortingDirection = 'desc',
    perPage,
    page
  }: {
    orderNos?: number[] | null;
    orderIds?: string[] | null;
    sortingColumn?: string;
    sortingDirection?: 'asc' | 'desc';
    perPage: number;
    page: number;
  }): OrderPaginationModel {
    const result: Partial<OrderPaginationModel> = {
      OrderNos: orderNos || undefined,
      OrderIds: orderIds || undefined,
      SortingColumn: sortingColumn,
      SortingDirection: sortingDirection,
      Paginate: {
        PerPage: perPage,
        Page: page
      }
    };


    if (this.currentSearchValue?.trim()) {
      switch (this.selectedSearchType) {
        // case 'common':
        //   result.CommonSearch = this.currentSearchValue;
        //   break;
        case 'orderNr':
          result.OrderNos = [Number(this.currentSearchValue)];
          break;
        case 'transactionNr':
          result.TransactionNos = [Number(this.currentSearchValue)];
          break;
        case 'name':
          result.NameSearch = this.currentSearchValue;
          break;
        case 'email':
          result.EmailSearch = this.currentSearchValue;
          break;
        case 'phone':
          result.PhoneSearch = this.currentSearchValue;
          break;
        case 'registration':
          result.LPSearch = this.currentSearchValue;
          break;
      }
    }

    // Backward compatibility for old search
    if (this.searchQuery?.trim()) {
      result.CommonSearch = this.searchQuery;
    }

    // Handle multi-select status
    if (this.selectedStatuses.length > 0) {
      result.Statuses = this.selectedStatuses;
    }

    if (this.selectedOrgs.length > 0) {
      result.OrganizationIds = this.selectedOrgs;
    } else {
      result.OrganizationIds = [this.organization.organizationId, ...this.availableSubOrgs.map(org => org.id)];
    }

    if (this.selectedOrderTypes.length > 0) {
      result.OrderTypes = this.selectedOrderTypes;
    }

    // Backward compatibility for single status
    // if (this.selectedStatus !== '') {
    //   result.Status = this.selectedStatus;
    // }

    // Handle date ranges from FormGroups
    const createdStart = this.createdDateRange.get('start')?.value;
    const createdEnd = this.createdDateRange.get('end')?.value;
    if (createdStart || createdEnd) {
      result.Created = {
        From: createdStart ? this.datepipe.transform(createdStart, 'yyyy-MM-dd') : null,
        To: createdEnd ? this.datepipe.transform(createdEnd, 'yyyy-MM-dd') : null
      };
    }

    const bookingBeginStart = this.bookingBeginRange.get('start')?.value;
    const bookingBeginEnd = this.bookingBeginRange.get('end')?.value;
    if (bookingBeginStart || bookingBeginEnd) {
      result.BookingBegin = {
        From: bookingBeginStart ? this.datepipe.transform(bookingBeginStart, 'yyyy-MM-dd') : null,
        To: bookingBeginEnd ? this.datepipe.transform(bookingBeginEnd, 'yyyy-MM-dd') : null
      };
    }

    const bookingEndStart = this.bookingEndRange.get('start')?.value;
    const bookingEndEnd = this.bookingEndRange.get('end')?.value;
    if (bookingEndStart || bookingEndEnd) {
      result.BookingEnd = {
        From: bookingEndStart ? this.datepipe.transform(bookingEndStart, 'yyyy-MM-dd') : null,
        To: bookingEndEnd ? this.datepipe.transform(bookingEndEnd, 'yyyy-MM-dd') : null
      };
    }

    return result as OrderPaginationModel;
  }

  // setLoadingState(isLoading: boolean) {
  //   this.isLoading = isLoading;
  // }

  // private incrementRequests() {
  //   this.activeRequests++;
  //   this.setLoadingState(true);
  // }

  // private decrementRequests() {
  //   if (this.activeRequests > 0)
  //     this.activeRequests--;


  setOrderMatTableData() {
    if (!this.organization) return;

    // Initialize data source
    // this.dataSource = new MatTableDataSource<Order>(this.orders);
    // this.dataSource.paginator = this.paginator;

    // Set up pagination changes
    if (this.paginator) {
      this.paginator.page.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.loadData();
      });
    }

    this.setupSorting();

    // Initial load
    this.loadData();
  }

  private setupSorting() {
    if (this.sort) {
      // Set default sort
      if (!this.sort.active) {
        this.sort.active = 'orderNr';
        this.sort.direction = 'desc';
        this.sort.sortChange.emit({
          active: this.sort.active,
          direction: this.sort.direction
        });
      }

      // Listen for sort changes
      this.sort.sortChange.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        if (this.paginator) {
          this.paginator.pageIndex = 0; // Reset to first page on sort
        }
        this.loadData();
      });
    }
  }

  private loadData() {
    if (!this.organization) return;

    // Get current sort state
    const sortActive = this.sort?.active || 'orderNr';
    const sortDirection = this.sort?.direction || 'desc';

    // Map to backend column name
    const sortColumn = this.columnMapping[sortActive] || sortActive;

    this.incrementRequests.emit();

    this.backofficeService.getPaginatedOrders(
      this.organization.organizationId,
      this.setFilters({
        perPage: this.paginator?.pageSize || 10,
        page: this.paginator ? this.paginator.pageIndex + 1 : 1,
        sortingColumn: sortColumn,
        sortingDirection: sortDirection as 'asc' | 'desc'
      })
    ).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error loading orders:', error);
        return of({ data: [], pagination: { total: 0 } });
      }),
      finalize(() => this.decrementRequests.emit())
    ).subscribe((response: any) => {
      if (!response) return;

      this.orders = response.data || [];
      this.totalData = response.pagination?.total || 0;

      // Process customer data
      this.orders.forEach(order => {
        if (order.customer) {
          order.customerName = `${order.customer.firstName} ${order.customer.lastName}`;
          order.cellular = order.customer.cellular;
          order.email = order.customer.email;
        }
      });

      // Update data source
      if (!this.dataSource) {
        this.dataSource = new MatTableDataSource<Order>(this.orders);
        this.dataSource.paginator = this.paginator;
      } else {
        this.dataSource.connect().next(this.orders);
      }
    });
  }

  goToOrder(orderId: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${orderId}`]));
    window.open(url, '_blank');
  }

  toggleFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
  }
}
